<script lang="ts" setup>
import { debounce } from 'lodash'

import { staffServices } from '@/api/staff'

type SelectRaw = string | number
type SelectValue = SelectRaw | SelectRaw[] | undefined

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Chọn nhân viên',
  },
  isMultiSelect: {
    type: Boolean,
    default: false,
  },
  filterBy: {
    type: Object,
    default: () => {},
  },
})
const val = defineModel<SelectValue>({
  default: [],
})

// API sẽ luôn trả về danh sách nhân viên có id thuộc danh sách sortSpecialId
const sortSpecialId = computed(() => {
  return val.value ? (Array.isArray(val.value) ? val.value : [val.value]) : []
})
// Loading state khi gọi API
const loading = ref(false)
// Danh sách nhân viên trả về từ API
const data = ref<any[]>([])

const pagination = reactive({
  currentPage: 0,
  pageSize: 20,
  total: 0,
})
const keyword = ref('')
const isLastPage = ref(false)

// Gán danh sách nhân viên đã chọn cho val model
const onChange = (value: any) => {
  val.value = value
}

// Thêm tùy chọn lọc staff từ props.filterBy. Ví dụ: { orgUnitId: 2 }
const additionFilter = computed(() => {
  return props.filterBy
})

// Hàm lấy dữ liệu nhân viên từ API
const getData = async (isAppend = false) => {
  if (loading.value || (isAppend && isLastPage.value)) return
  loading.value = true
  console.log(`Fetching staff data... Page: ${pagination.currentPage}, isAppend: ${isAppend}`)

  if (!isAppend) {
    pagination.currentPage = 0
    data.value = []
    isLastPage.value = false
  }

  const defaultExample = { keyword: keyword.value, sortSpecialId: sortSpecialId.value }
  const filters = {
    example: { ...defaultExample, ...additionFilter.value },
    pageInfo: { currentPage: pagination.currentPage, pageSize: pagination.pageSize },
  }

  try {
    const { body } = await staffServices.search(filters)
    const result = (body.result || []).map((item: any) => {
      return {
        label: item.staffName,
        value: item.staffId,
      }
    })

    if (isAppend) {
      data.value = [...data.value, ...result]
    } else {
      data.value = result
    }

    const totalRecords = body.pageInfo?.totalRecords || body.totalRecords || 0
    pagination.total = totalRecords

    // Nếu có trả về totalRecords và đã load đủ thì dừng
    if (totalRecords > 0 && data.value.length >= totalRecords) {
      isLastPage.value = true
    }
    // Nếu kết quả trả về ít hơn pageSize thì chắc chắn là trang cuối
    else if (result.length < pagination.pageSize) {
      isLastPage.value = true
    } else {
      isLastPage.value = false
    }
    console.log(
      `Loaded ${result.length} items. Total: ${pagination.total}, isLastPage: ${isLastPage.value}`
    )
  } catch (error) {
    console.error('Failed to fetch staff data:', error)
  } finally {
    loading.value = false
  }
}
const onSearch = debounce(async (value: string) => {
  keyword.value = value
  await getData()
}, 300)

const onPopupScroll = (e: any) => {
  const { target } = e
  const isBottom = Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) < 1
  // console.log('Scroll:', target.scrollTop + target.clientHeight, '>=', target.scrollHeight - 50)
  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50 || isBottom) {
    if (!loading.value && !isLastPage.value) {
      console.log('Loading next page...')
      pagination.currentPage++
      getData(true)
    }
  }
}

onMounted(async () => {
  await getData()
})
</script>
<template>
  <a-select
    v-model:value="val"
    allow-clear
    show-search
    :options="data"
    :loading="loading"
    :mode="isMultiSelect ? 'multiple' : undefined"
    :placeholder="placeholder"
    :filter-option="false"
    :not-found-content="null"
    :virtual="false"
    @search="onSearch"
    @change="onChange"
    @popupScroll="onPopupScroll"
  />
</template>
